import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, inject, Input, Output, signal, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors, Validator } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

import { SasMarkOnSubmitDirective } from '@shared/directives/sas-mark-on-submit.directive';
import { FormValidation } from '@shared/interfaces/form-validation';
import { TemplateDrivenFieldService } from '@shared/services/template-driven-field.service';

@Component({
    selector: 'sas-label-value',
    imports: [TranslatePipe, FormsModule, SasMarkOnSubmitDirective, NgbPopover],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SasLabelValueComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SasLabelValueComponent),
            multi: true
        }
    ],
    templateUrl: './sas-label-value.component.html',
    styleUrl: './sas-label-value.component.scss'
})
export class SasLabelValueComponent implements ControlValueAccessor, Validator, AfterViewInit {
    @Input() name: string = '';
    @Input() label: string = '';
    @Input() model: string = '';
    @Input() sasClass: string = '';
    @Input() isEditable: boolean = false;
    @Input() hideLabel: boolean = false;
    @Input() validation?: FormValidation;
    @Input() formId?: string = '';
    @Output() modelChange = new EventEmitter<any>();
    @ViewChild('editableInput') element!: NgModel;
    @ViewChild('sasValue') sasValue!: ElementRef<HTMLElement>;

    templateDrivenFieldService = inject(TemplateDrivenFieldService);

    protected readonly isOverflowing = signal(false);

    readonly random = Math.random().toString(36).substring(2);
    value: any;
    onChange = (_: any) => { };
    onTouched = () => { };

    ngOnChanges(changes: any) {
        if (changes.model) {
            this.value = this.model;
            setTimeout(() => this.checkOverflow());
        }
    }

    ngOnInit() {
        this.value = this.model;
    }

    ngAfterViewInit() {
        this.checkOverflow();
    }

    private checkOverflow() {
        if (this.sasValue) {
            const el = this.sasValue.nativeElement;
            this.isOverflowing.set(el.scrollHeight > el.clientHeight);
        }
    }

    writeValue(value: any): void {
        this.value = value;
        this.model = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    onModelChange(value: any) {
        this.value = value;
        this.model = value;
        this.onChange(value);
        this.onTouched();
        this.modelChange.emit(value);
    }

    sendModel(): any {
        this.modelChange.emit(this.model);
    }

    markAsPristine() {
        if (this.element) {
            this.element.control.markAsPristine();
            this.element.control.markAsUntouched();
        }
    }

    markAsDirty() {
        if (this.element) {
            this.element.control.markAsDirty();
            this.element.control.markAsTouched();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        if (this.validation?.required &&
            (this.value === null || this.value === undefined || this.value === '')
        ) {
            errors['required'] = true;
        }

        // pattern, minlength, etc...
        if (this.element?.errors) {
            Object.assign(errors, this.element.errors);
        }

        return Object.keys(errors).length ? errors : null;
    }
}
